/**
 * Signing out, in one place for every Sign out control (the top-bar icon, the
 * profile menu in the side menu and phone drawer, Your account, and "More" in
 * simple mode).
 *
 * - Count entries still waiting to upload are never thrown away silently:
 *   the person is warned first and can sync, or sign out anyway.
 * - The server ends the session (POST /api/auth/logout deletes it and clears
 *   the HttpOnly cookie); the in-memory cache is always cleared.
 * - Offline data on this device (cached core, queued entries, count drafts) is
 *   cleared only when nothing is waiting to upload. Signing out anyway keeps
 *   the waiting entries so they can still be sent after the next sign-in.
 */
export const unsyncedWarning = (n: number) =>
  `You have ${n} ${n === 1 ? "entry" : "entries"} not yet uploaded — sync first or sign out anyway.`;

export type SignOutDeps = {
  /** Count entries on this device that have not reached the server yet. */
  unsynced: () => Promise<number>;
  /** Ends the session on the server. */
  endSession: () => Promise<void>;
  /** Clears the in-memory query and core cache. */
  clearCache: () => void;
  /** Clears this device's offline data; with keepUnsynced, only the cached copy of the workspace. */
  clearOffline: (options: { keepUnsynced: boolean }) => Promise<void>;
};
export type SignOutResult =
  | { status: "needs-confirmation"; unsynced: number; message: string }
  | { status: "signed-out"; keptUnsynced: number };

export async function signOut(deps: SignOutDeps, options: { anyway?: boolean } = {}): Promise<SignOutResult> {
  const unsynced = await deps.unsynced().catch(() => 0);
  if (unsynced > 0 && !options.anyway) return { status: "needs-confirmation", unsynced, message: unsyncedWarning(unsynced) };
  // If the server cannot be reached this throws and nothing is cleared: the person stays signed in and sees why.
  await deps.endSession();
  deps.clearCache();
  await deps.clearOffline({ keepUnsynced: unsynced > 0 });
  return { status: "signed-out", keptUnsynced: unsynced };
}
